import React, { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';

import {
    BaseAttribute,
    DIYAttribute,
    FashionAttributes,
    HomeGardenttributes,
    PhoneBrandsWithModelChoice,
    phoneBrandsWithModelChoice,
    PhonesAttribute,
} from '@gumtree/shared/src/types/attributes';

import { VipState } from '../../reducers';
import {
    AttributeCell,
    AttributesContainer,
    AttributeText,
    Container,
    Title,
    Flex,
} from './index.style';
import StorageCapacity from './icons/storage-capacity';
import Condition from './icons/condition';
import Colour from './icons/colour';
import Model from './icons/model';
import MaterialIcon from './icons/material';
import HomeGardenColour from './icons/home-garden-colour';
import HomeGardenCondition from './icons/home-garden-condition';
import TourserLength from './icons/tourser-length';
import WaistSize from './icons/waist-size';
import SizeAccessory from './icons/size-accessory';
import SizeClothes from './icons/size-clothes';
import Gender from './icons/gender';
import Age from './icons/age';
import SizeTrouser from './icons/size-trouser';
import SizeShoes from './icons/size-shoes';
import SizeUnderwear from './icons/size-underwear';
import Brand from './icons/brand';
import BabyCarSeat from './icons/baby-car-seat';
import Diaper from './icons/diaper';
import Cot from './icons/cot';
import BabyAge from './icons/baby-age';

const attributeConfig: Record<
    PhonesAttribute['key'] &
        DIYAttribute['key'] &
        HomeGardenttributes['key'] &
        FashionAttributes['key'],
    {
        icon: React.ComponentType;
    }
> = {
    ...(Object.fromEntries(
        phoneBrandsWithModelChoice.map((brand) => [
            brand,
            {
                icon: Model as React.ComponentType,
            },
        ])
    ) as Record<
        PhoneBrandsWithModelChoice,
        {
            icon: React.ComponentType;
        }
    >),
    mobile_condition: {
        icon: Condition,
    },
    mobile_storage_capacity: {
        icon: StorageCapacity,
    },
    mobile_colour: {
        icon: Colour,
    },
    diy_tools_materials_condition: {
        icon: Condition,
    },
    common_for_sale_condition: {
        icon: HomeGardenCondition,
    },
    common_for_sale_colour: {
        icon: HomeGardenColour,
    },
    common_for_sale_material: {
        icon: MaterialIcon,
    },
    // fashion
    fashion_brand: {
        icon: Brand,
    },
    common_for_sale_colour_mandatory: {
        icon: Colour,
    },
    common_for_sale_gender: {
        icon: Gender,
    },
    kid_age: {
        icon: Age,
    },
    baby_age: {
        icon: Age,
    },
    male_shoe_size: {
        icon: SizeShoes,
    },
    female_shoe_size: {
        icon: SizeShoes,
    },
    accessory_size: {
        icon: SizeAccessory,
    },
    female_clothing_size: {
        icon: SizeClothes,
    },
    female_bra_size: {
        icon: SizeUnderwear,
    },
    male_clothing_size: {
        icon: SizeClothes,
    },
    male_formal_shirt_size: {
        icon: SizeClothes,
    },
    male_suit_size: {
        icon: SizeClothes,
    },
    male_suit_waist_size: {
        icon: WaistSize,
    },
    male_coat_size: {
        icon: SizeClothes,
    },
    male_tourser_waist_size: {
        icon: SizeTrouser,
    },
    male_tourser_length: {
        icon: TourserLength,
    },
    male_short_size: {
        icon: SizeClothes,
    },
    baby_shoe_size: {
        icon: SizeShoes,
    },
    kid_shoe_size: {
        icon: SizeShoes,
    },
    // P9
    condition_with_only_new_value: {
        icon: Condition,
    },
    baby_kids_stuff_brand: {
        icon: Brand,
    },
    baby_nappy_size: {
        icon: Diaper,
    },
    baby_car_seat_size: {
        icon: BabyCarSeat,
    },
    baby_bedding_size: {
        icon: Cot,
    },
    age_group: {
        icon: BabyAge,
    },
    sport_stuff_brand: {
        icon: Brand,
    },
    sport_shoe_size: {
        icon: SizeShoes,
    },
    common_clothing_size_optional: {
        icon: SizeClothes,
    },
    appliance_brand: {
        icon: Brand,
    },
};

const getPhoneSpecificAttributes = (attributes: BaseAttribute[] = []) => {
    return attributes.filter(
        (attr) => attr && attr.key && attributeConfig[attr.key]
    ) as PhonesAttribute[];
};

const getIconByAttributeKey = (key: string): React.ComponentType | undefined => {
    return key ? attributeConfig[key]?.icon : undefined;
};

const PhonesAttributes: React.FC<PropsWithChildren<{ isMobileCardsLayout?: boolean }>> = ({
    isMobileCardsLayout = false,
    children,
}) => {
    const { attributes = [] } = useSelector(({ adDetails: { attributes = [] } }: VipState) => ({
        attributes: attributes as BaseAttribute[],
    }));

    const phoneSpecificAttributes = getPhoneSpecificAttributes(attributes);

    if (!phoneSpecificAttributes || phoneSpecificAttributes.length === 0) {
        return <></>;
    } else {
        return (
            <Container isMobileCardsLayout={isMobileCardsLayout}>
                <Flex>
                    <Title>Details</Title>
                    {children}
                </Flex>
                <AttributesContainer>
                    {phoneSpecificAttributes.map((attribute) => {
                        const Icon = attribute?.key
                            ? getIconByAttributeKey(attribute.key)
                            : undefined;
                        return (
                            <AttributeCell key={attribute.key}>
                                {Icon && <Icon />}
                                <AttributeText>{`${attribute.name}: ${attribute.value}`}</AttributeText>
                            </AttributeCell>
                        );
                    })}
                </AttributesContainer>
            </Container>
        );
    }
};

export default PhonesAttributes;
